export interface LivenessSession {
  sessionId: string;
  status: 'created' | 'active' | 'completed' | 'failed';
  createdAt: string;
}

export interface LivenessVerificationResult {
  success: boolean;
  sessionId: string;
  confidence?: number;
  message: string;
  status?: string;
  referenceImage?: any;
  auditImages?: any[];
}

export interface AmplifyLivenessConfig {
  region: string;
  accessKeyId?: string;
  secretAccessKey?: string;
  sessionToken?: string;
}

export class LivenessService {
  public static API_BASE_URL = "https://faceapp.cditproject.org/api";

  /**
   * Create a new liveness session using CreateFaceLivenessSession
   * This calls the backend which creates an AWS Rekognition Face Liveness session
   * @returns Promise<LivenessSession>
   */
  static async createSession(): Promise<LivenessSession> {
    try {
      console.log("🚀 LivenessService: Creating new AWS Face Liveness session...");

      const response = await fetch(`${this.API_BASE_URL}/liveness/session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          // Optional settings for CreateFaceLivenessSession
          settings: {
            auditImagesLimit: 2, // Store up to 2 audit images
            challengePreferences: [{
              type: "FaceLivenessMovementAndLightChallenge",
              versions: {
                minimum: "1.0.0",
                maximum: "1.0.0"
              }
            }]
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ LivenessService: Session creation failed:", errorText);
        throw new Error(`Failed to create liveness session: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ LivenessService: AWS session created successfully:", result);

      const session: LivenessSession = {
        sessionId: result.session_id || result.sessionId || result.SessionId,
        status: 'created',
        createdAt: new Date().toISOString(),
      };

      console.log("📋 Session details:", session);
      return session;
    } catch (error) {
      console.error("❌ LivenessService: Error creating session:", error);
      throw error;
    }
  }

  /**
   * Verify liveness using GetFaceLivenessSessionResults
   * This is called after the FaceLivenessDetector completes the video capture
   * and AWS processes the StartFaceLivenessSession
   * @param sessionId - The session ID from createSession
   * @returns Promise<LivenessVerificationResult>
   */
  static async verifyLiveness(sessionId: string): Promise<LivenessVerificationResult> {
    try {
      console.log("🔍 LivenessService: Getting liveness session results for:", sessionId);

      if (!sessionId) {
        throw new Error("Session ID is required for liveness verification");
      }

      const response = await fetch(`${this.API_BASE_URL}/liveness/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          session_id: sessionId,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ LivenessService: Verification failed:", errorText);
        
        let errorMessage = `Liveness verification failed: ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch (parseError) {
          // Keep default error message
        }

        return {
          success: false,
          sessionId,
          message: errorMessage,
        };
      }

      const result = await response.json();
      console.log("📊 LivenessService: Verification response:", result);

      // AWS GetFaceLivenessSessionResults response format
      const isSuccess = result.success || result.Status === 'SUCCEEDED' || result.confidence > 80;
      
      return {
        success: isSuccess,
        sessionId,
        confidence: result.confidence || result.Confidence,
        message: result.message || (isSuccess ? 'Liveness verification successful' : 'Liveness verification failed'),
        status: result.Status || result.status,
        referenceImage: result.ReferenceImage || result.referenceImage,
        auditImages: result.AuditImages || result.auditImages,
      };
    } catch (error) {
      console.error("❌ LivenessService: Error verifying liveness:", error);
      return {
        success: false,
        sessionId,
        message: error instanceof Error ? error.message : 'Network error during liveness verification',
      };
    }
  }

  /**
   * Complete liveness flow: create session and verify
   * Note: The FaceLivenessDetector handles the StartFaceLivenessSession automatically
   * @returns Promise<LivenessVerificationResult>
   */
  static async completeLivenessFlow(): Promise<LivenessVerificationResult> {
    try {
      console.log("🔄 LivenessService: Starting complete liveness flow...");

      // Step 1: Create session (calls CreateFaceLivenessSession on backend)
      const session = await this.createSession();
      console.log("✅ LivenessService: Session created, FaceLivenessDetector will handle video capture...");

      // Note: Steps 2 (StartFaceLivenessSession) is handled by FaceLivenessDetector
      // Step 3 (GetFaceLivenessSessionResults) is handled by verifyLiveness
      
      return {
        success: true,
        sessionId: session.sessionId,
        message: 'Session created successfully. Proceed with FaceLivenessDetector.',
      };
    } catch (error) {
      console.error("❌ LivenessService: Error in complete liveness flow:", error);
      throw error;
    }
  }
}

export default LivenessService;